/**
 * API Endpoint Constants
 *
 * Central registry of all API endpoints.
 * Used by domain API clients for type-safe endpoint references.
 */

export const API_ENDPOINTS = {
  // Auth
  CSRF_TOKEN: '/api/v1/csrf-token',
  AUTH_WEBHOOK: '/api/v1/auth/webhook',

  // Portal (SSO bridge — embedded → standalone)
  PORTAL_LINK: '/api/v1/portal/link',
  // Обмен claim-билета на привязку Clerk-аккаунта к портальному пользователю.
  // Не под /embedded: сюда приходят уже из веб-версии, с Clerk-JWT.
  PORTAL_CLAIM: '/api/v1/portal-claim',

  // Users
  USERS_ME: '/api/v1/users/me',
  // Поп-ап «что нового» (#946). В embedded путь переписывается на
  // /api/v1/embedded/... — см. useEmbeddedAuthFetch.
  USERS_ME_SEEN_RELEASE: '/api/v1/users/me/seen-release',
  USERS: '/api/v1/users',
  USERS_BY_ID: (id: string) => `/api/v1/users/${id}`,
  USERS_LINK_ORG: '/api/v1/users/link-org',
  USERS_INVITE: '/api/v1/users/invite',
  USERS_ROLE: (id: string) => `/api/v1/users/${id}/role`,
  USERS_PROJECTS: (id: string) => `/api/v1/users/${id}/projects`,

  // Agents (Performance & Leaderboard)
  AGENTS_ME_PERFORMANCE: '/api/v1/agents/me/performance',
  AGENTS_ME_ISSUES: '/api/v1/agents/me/issues',
  AGENTS_ME_FEEDBACK_EFFECT: '/api/v1/agents/me/feedback-effect',
  AGENTS_ME_COMPETITION: '/api/v1/agents/me/competition',
  AGENTS_BY_ID_PERFORMANCE: (agentId: string) => `/api/v1/agents/${agentId}/performance`,
  AGENTS_TEAMS_LEADERBOARD: '/api/v1/agents/teams/leaderboard',
  AGENTS_SCRIPT_ADHERENCE: '/api/v1/agents/script-adherence',
  AGENTS_OBJECTIONS: '/api/v1/agents/objections',
  AGENTS_OBJECTIONS_STREAM: '/api/v1/agents/objections/stream',

  // Coaching
  AGENTS_ME_COACHING_INSIGHTS: '/api/v1/agents/me/coaching/insights',
  AGENTS_BY_ID_COACHING_INSIGHTS: (agentId: string) =>
    `/api/v1/agents/${agentId}/coaching/insights`,
  AGENTS_ME_COACHING_WEEKLY: '/api/v1/agents/me/coaching/weekly',
  AGENTS_ME_COACHING_NOTES: '/api/v1/agents/me/coaching/notes',
  AGENTS_ME_COACHING_NOTES_SENT: '/api/v1/agents/me/coaching/notes/sent',
  AGENTS_ME_COACHING_SESSIONS: '/api/v1/agents/me/coaching/sessions',
  AGENTS_BY_ID_COACHING_NOTES: (agentId: string) =>
    `/api/v1/agents/${agentId}/coaching/notes`,
  AGENTS_COACHING_NOTES: '/api/v1/agents/coaching/notes',
  AGENTS_COACHING_STATS: '/api/v1/agents/coaching/stats',
  AGENTS_COACHING_SESSIONS: '/api/v1/agents/coaching/sessions',

  // Analytics
  ANALYTICS_TEAM_METRICS: '/api/v1/analytics/team-metrics',
  ANALYTICS_CONFIG_EVENTS: '/api/v1/analytics/config-events',
  ANALYTICS_QUALITY_DISTRIBUTION: '/api/v1/analytics/quality-distribution',
  ANALYTICS_CALL_STATS: '/api/v1/analytics/call-stats',
  ANALYTICS_MISSED_CALLS: '/api/v1/analytics/missed-calls',
  ANALYTICS_SERIES_FUNNEL: '/api/v1/analytics/call-series-funnel',
  ANALYTICS_OUTCOME_FUNNEL: '/api/v1/analytics/outcome-funnel',

  // Reports — вкладка «Головне» (#831)
  REPORTS_SUMMARY: '/api/v1/reports/summary',
  REPORTS_FLAGGED: '/api/v1/reports/flagged',
  REPORTS_EMPLOYEE_ISSUES: '/api/v1/reports/employee-issues',
  REPORTS_EMPLOYEE_FEEDBACK_EFFECT: '/api/v1/reports/employee-feedback-effect',
  // «Голос клиента» (#848)
  ANALYTICS_VOICE: '/api/v1/analytics/voice',
  // Ряды «Голоса клиента» по корзинам (#2394 §9 п. 5)
  ANALYTICS_VOICE_SERIES: '/api/v1/analytics/voice/series',
  // Набор тем организации (#845)
  VOICE_THEMES: '/api/v1/analytics/voice/themes',
  VOICE_THEME_BY_ID: (id: string) =>
    `/api/v1/analytics/voice/themes/${encodeURIComponent(id)}`,
  VOICE_THEME_ARCHIVE: (id: string) =>
    `/api/v1/analytics/voice/themes/${encodeURIComponent(id)}/archive`,
  VOICE_THEME_UNARCHIVE: (id: string) =>
    `/api/v1/analytics/voice/themes/${encodeURIComponent(id)}/unarchive`,
  // Стартовое предложение набора (#845, PR 4)
  VOICE_THEME_BOOTSTRAP: '/api/v1/analytics/voice/themes/bootstrap',
  VOICE_THEME_BOOTSTRAP_START: '/api/v1/analytics/voice/themes/bootstrap/start',
  VOICE_THEME_BOOTSTRAP_REGENERATE: '/api/v1/analytics/voice/themes/bootstrap/regenerate',
  VOICE_THEME_BOOTSTRAP_APPROVE: '/api/v1/analytics/voice/themes/bootstrap/approve',
  // Предложения новых тем недельной сводки (#845, PR 5b)
  VOICE_THEME_PROPOSALS: '/api/v1/analytics/voice/themes/proposals',
  VOICE_THEME_PROPOSAL_ACCEPT: (id: string) =>
    `/api/v1/analytics/voice/themes/proposals/${encodeURIComponent(id)}/accept`,
  VOICE_THEME_PROPOSAL_REJECT: (id: string) =>
    `/api/v1/analytics/voice/themes/proposals/${encodeURIComponent(id)}/reject`,
  // Отчёт по критериям (#866): сводка; один критерий — `/${id}`.
  REPORTS_CRITERIA: '/api/v1/reports/criteria',
  // Отчёт по шагам скрипта (#879): сводка; один шаг — `/${stepId}`.
  REPORTS_SCRIPT_STEPS: '/api/v1/reports/script-steps',

  // Projects
  PROJECTS: '/api/v1/projects',
  PROJECTS_BY_ID: (id: string) => `/api/v1/projects/${id}`,
  PROJECTS_SETTINGS: (id: string) => `/api/v1/projects/${id}/settings`,
  PROJECTS_STATS: (id: string) => `/api/v1/projects/${id}/stats`,
  PROJECTS_SUPERVISORS: (id: string) => `/api/v1/projects/${id}/supervisors`,
  PROJECTS_ROLES: (id: string) => `/api/v1/projects/${id}/roles`,
  PROJECTS_ARCHIVE: (id: string) => `/api/v1/projects/${id}/archive`,
  PROJECTS_CHANNEL_SETTINGS: (id: string) => `/api/v1/projects/${id}/channel-settings`,
  // Team alert-delivery channels (email/telegram/webhook/bitrix24_chat/inApp
  // + severity threshold) — distinct from PROJECTS_CHANNEL_SETTINGS above,
  // which is the unrelated call/chat communication-channel concept.
  PROJECTS_ALERT_NOTIFICATION_CHANNELS: (id: string) =>
    `/api/v1/projects/${id}/alert-notification-channels`,
  PROJECTS_ALERT_NOTIFICATION_CHANNELS_TEST: (id: string) =>
    `/api/v1/projects/${id}/alert-notification-channels/test`,
  // Weights of the six quality criteria in the project's score (#874).
  PROJECTS_QUALITY_WEIGHTS: (id: string) => `/api/v1/projects/${id}/quality-weights`,
  PROJECTS_QUALITY_WEIGHTS_PREVIEW: (id: string) =>
    `/api/v1/projects/${id}/quality-weights/preview`,

  // Records
  RECORDS: '/api/v1/records',
  RECORDS_FILTER_OPTIONS: '/api/v1/records/filter-options',
  RECORDS_CRITERIA_FILTER_OPTIONS: '/api/v1/records/criteria-filter-options',
  RECORDS_BY_ID: (id: string) => `/api/v1/records/${id}`,
  RECORDS_ARCHIVE: (id: string) => `/api/v1/records/${id}/archive`,
  RECORDS_AGENT_RATING: (id: string) => `/api/v1/records/${id}/agent-rating`,
  RECORDS_UPLOAD: '/api/v1/records/upload',
  RECORDS_ESTIMATE_COST: '/api/v1/records/estimate-cost',
  // Накопленное до включения анализа: объём с ценой и его запуск.
  RECORDS_BACKLOG_SUMMARY: '/api/v1/records/backlog-summary',
  RECORDS_PROCESS_BACKLOG: '/api/v1/records/process-backlog',
  // Ручной режим: запуск выбранных записей и цена этого набора.
  RECORDS_START: '/api/v1/records/start',
  RECORDS_START_QUOTE: '/api/v1/records/start-quote',
  RECORDS_RETRY_MEDIA: (id: string) => `/api/v1/records/${id}/retry-media`,
  RECORDS_RETRY_ANALYSIS: (id: string) => `/api/v1/records/${id}/retry-analysis`,
  RECORDS_REANALYZE: (id: string) => `/api/v1/records/${id}/reanalyze`,
  RECORDS_RETRY_TRANSCRIPTION: (id: string) =>
    `/api/v1/records/${id}/retry-transcription`,
  RECORDS_SCRIPT_ADHERENCE: (id: string) => `/api/v1/records/${id}/script-adherence`,
  RECORDS_CRITERIA: (id: string) => `/api/v1/records/${id}/criteria`,
  // Custom call criteria (#857) and the wording trial (#861)
  CALL_CRITERIA: '/api/v1/call-criteria',
  CALL_CRITERIA_BY_ID: (id: string) => `/api/v1/call-criteria/${id}`,
  CALL_CRITERIA_ARCHIVE: (id: string) => `/api/v1/call-criteria/${id}/archive`,
  CALL_CRITERIA_TRIAL: '/api/v1/call-criteria/trial',
  // Assistant: draft from a description, revision from marked answers (#1416)
  CALL_CRITERIA_DRAFT: '/api/v1/call-criteria/draft',
  CALL_CRITERIA_REVISE: '/api/v1/call-criteria/revise',
  CALL_CRITERIA_ACTIVITY: '/api/v1/call-criteria/activity',
  RECORDS_AUDIO: (id: string) => `/api/v1/records/${id}/audio`,
  RECORDS_TRANSCRIPT: (id: string) => `/api/v1/records/${id}/transcript`,
  // Analyses
  ANALYSES: '/api/v1/analyses',
  ANALYSES_BY_ID: (id: string) => `/api/v1/analyses/${id}`,
  ANALYSES_PDF_REPORT: (id: string) => `/api/v1/analyses/${id}/report/pdf`,
  ANALYSES_ANNOTATIONS: (id: string) => `/api/v1/analyses/${id}/annotations`,
  ANALYSES_REVIEW: (id: string) => `/api/v1/analyses/${id}/review`,
  ANALYSES_ANNOTATION_BY_ID: (analysisId: string, annotationId: string) =>
    `/api/v1/analyses/${analysisId}/annotations/${annotationId}`,

  // Alerts
  ALERTS: '/api/v1/alerts',
  ALERTS_BY_ID: (id: string) => `/api/v1/alerts/${id}`,
  ALERTS_CRITERION_EVIDENCE: (id: string) => `/api/v1/alerts/${id}/criterion-evidence`,
  ALERTS_ATTENTION_MOMENT: (id: string) => `/api/v1/alerts/${id}/attention-moment`,
  ALERTS_ACKNOWLEDGE: (id: string) => `/api/v1/alerts/${id}/acknowledge`,
  ALERTS_RESOLVE: (id: string) => `/api/v1/alerts/${id}/resolve`,
  ALERTS_CRM_ACTION: (id: string) => `/api/v1/alerts/${id}/actions/crm`,
  ALERTS_BULK_RESOLVE: '/api/v1/alerts/bulk-resolve',
  ALERTS_RULES: '/api/v1/alerts/rules',
  ALERTS_RULES_BY_ID: (id: string) => `/api/v1/alerts/rules/${id}`,

  // Incidents
  INCIDENTS: '/api/v1/incidents',
  INCIDENTS_BY_ID: (id: string) => `/api/v1/incidents/${id}`,
  INCIDENTS_RESOLVE: (id: string) => `/api/v1/incidents/${id}/resolve`,

  // Consent
  CONSENT: '/api/v1/consent',
  CONSENT_STATUS: '/api/v1/consent/status',
  CONSENT_MARKETING: '/api/v1/consent/marketing',
  CONSENT_GRANT: '/api/v1/consent/grant',
  CONSENT_WITHDRAW: '/api/v1/consent/withdraw',
  CONSENT_CONFIRM_COMPLIANCE: '/api/v1/consent/confirm-compliance',

  // Support chat
  SUPPORT_CHAT_IDENTITY: '/api/v1/support/chat-identity',

  // DSR
  DSR: '/api/v1/dsr',
  DSR_BY_ID: (id: string) => `/api/v1/dsr/${id}`,
  DSR_APPROVE: (id: string) => `/api/v1/dsr/${id}/approve`,
  DSR_REJECT: (id: string) => `/api/v1/dsr/${id}/reject`,
  DSR_EXECUTE: (id: string) => `/api/v1/dsr/${id}/execute`,

  // Human Review
  HUMAN_REVIEW: '/api/v1/human-review',
  HUMAN_REVIEW_APPROVE: (id: string) => `/api/v1/human-review/${id}/approve`,
  HUMAN_REVIEW_REJECT: (id: string) => `/api/v1/human-review/${id}/reject`,
  HUMAN_REVIEW_RETRY: (id: string) => `/api/v1/human-review/${id}/retry`,

  // Deals (#766): the queue, the card, its conversation feed, the CRM task
  DEALS: '/api/v1/deals',
  // The deal state of a CRM entity for the Bitrix24 card tab (#745). Embedded
  // only: the embedded fetch rewrites it to /api/v1/embedded/deals/by-crm-entity.
  DEALS_BY_CRM_ENTITY: '/api/v1/deals/by-crm-entity',
  DEAL_CARD: (stateId: string) => `/api/v1/deals/${encodeURIComponent(stateId)}`,
  DEAL_CONVERSATIONS: (stateId: string) =>
    `/api/v1/deals/${encodeURIComponent(stateId)}/conversations`,
  DEAL_CRM_TASK: (stateId: string) =>
    `/api/v1/deals/${encodeURIComponent(stateId)}/crm-task`,
  // The draft message to the client by the deal's head state (#1675)
  DEAL_CLIENT_MESSAGE: (stateId: string) =>
    `/api/v1/deals/${encodeURIComponent(stateId)}/client-message`,

  // Dashboard
  DASHBOARD: '/api/v1/dashboard',
  DASHBOARD_QUALITY: '/api/v1/dashboard/quality',
  DASHBOARD_QUALITY_SECTIONS: '/api/v1/dashboard/quality/sections',
  DASHBOARD_FLOW: '/api/v1/dashboard/flow',
  DASHBOARD_TEAM: '/api/v1/dashboard/team',
  DASHBOARD_SALES: '/api/v1/dashboard/sales',
  DASHBOARD_VOICE: '/api/v1/dashboard/voice',
  // Settings → «Работа сервиса» (#1341)
  DASHBOARD_SERVICE: '/api/v1/dashboard/service',

  // Billing
  BILLING_SUMMARY: '/api/v1/billing/summary',
  BILLING_BUDGET: (orgId: string) => `/api/v1/billing/budget/${orgId}`,
  BILLING_BALANCE: (orgId: string) => `/api/v1/billing/balance/${orgId}`,
  BILLING_LEDGER: (orgId: string) => `/api/v1/billing/ledger/${orgId}`,
  BILLING_USAGE: (orgId: string) => `/api/v1/billing/usage/${orgId}`,
  BILLING_PRICING: (orgId: string) => `/api/v1/billing/pricing/${orgId}`,
  BILLING_MARGIN: (orgId: string) => `/api/v1/billing/margin/${orgId}`,
  BILLING_CREDITS: '/api/v1/billing/credits',
  BILLING_AUTO_RECHARGE: (orgId: string) => `/api/v1/billing/auto-recharge/${orgId}`,
  BILLING_AUTO_RECHARGE_SETUP: (orgId: string) =>
    `/api/v1/billing/auto-recharge/${orgId}/setup`,
  BILLING_PAUSE_OFFER: (orgId: string) => `/api/v1/billing/pause-offer/${orgId}`,
  BILLING_FIRST_TOPUP_OFFER: (orgId: string) =>
    `/api/v1/billing/first-topup-offer/${orgId}`,
  BILLING_TIER_ADVICE: (orgId: string) => `/api/v1/billing/tier-advice/${orgId}`,

  // Settings
  SETTINGS: '/api/v1/settings',
  // How many of THIS org's records a proposed retention window would delete —
  // owner-only, read before a retention change is confirmed.
  SETTINGS_RETENTION_IMPACT: (days: number) =>
    `/api/v1/settings/retention-impact?days=${days}`,

  // Organizations
  ORGANIZATIONS_METRICS: (orgId: string) => `/api/v1/organizations/${orgId}/metrics`,

  // Admin
  ADMIN_ORGANIZATIONS: '/api/v1/admin/organizations',
  ADMIN_ORGANIZATIONS_CREDITS: (orgId: string) =>
    `/api/v1/admin/organizations/${orgId}/credits`,
  ADMIN_PROVIDER_CREDENTIALS: '/api/v1/admin/provider-credentials',
  ADMIN_PROVIDER_CREDENTIALS_BY_TYPE: (type: string, name: string) =>
    `/api/v1/admin/provider-credentials/${type}/${name}`,
  ADMIN_PLATFORM_SETTINGS: '/api/v1/admin/platform-settings',
  ADMIN_PLATFORM_SETTINGS_DEFAULTS: '/api/v1/admin/platform-settings/defaults',
  ADMIN_PLATFORM_SETTINGS_RETENTION: '/api/v1/admin/platform-settings/retention',
  ADMIN_PLATFORM_SETTINGS_RETENTION_IMPACT: (days: number) =>
    `/api/v1/admin/platform-settings/retention/impact?days=${days}`,
  ADMIN_PLATFORM_SETTINGS_BY_KEY: (key: string) =>
    `/api/v1/admin/platform-settings/${key}`,

  // Health
  HEALTH_FAILED_DELIVERIES: '/api/v1/health/failed-deliveries',
  HEALTH_INTEGRATIONS: '/api/v1/health/integrations',
  HEALTH_DELIVERY_RETRY: (id: string) => `/api/v1/health/deliveries/${id}/retry`,

  // Invitations
  INVITATIONS_LIST: '/api/v1/invitations/list',
  INVITATIONS_REVOKE: (id: string) => `/api/v1/invitations/revoke/${id}`,
  INVITATIONS_RESEND: (id: string) => `/api/v1/invitations/resend/${id}`,
  INVITATIONS_BY_TOKEN: (token: string) => `/api/v1/invitations/${token}`,
  INVITATIONS_ACCEPT: (token: string) => `/api/v1/invitations/${token}/accept`,

  // Feature Flags
  FEATURE_FLAGS: '/api/v1/feature-flags',

  // «Мой доступ» (E1.5, #1336, #1645): режим, возможности и область гранта.
  // В embedded путь переписывается на /api/v1/embedded/access/me.
  ACCESS_ME: '/api/v1/access/me',

  // Назначение и отзыв узкого доступа (E1.5 PR 5, #1336): «Настройки → Команда».
  ACCESS_GRANTS: '/api/v1/access/grants',
  ACCESS_DEPARTMENTS: '/api/v1/access/departments',
  // Журнал доступа организации (#2492): «Настройки → Журнал доступа».
  ACCESS_EVENTS: '/api/v1/access/events',
  USERS_ACCESS: (id: string) => `/api/v1/users/${id}/access`,

  // Embedded
  EMBEDDED_AUTH: '/api/v1/embedded/auth',
  EMBEDDED_SESSION: '/api/v1/embedded/session',
  EMBEDDED_SESSION_BOOTSTRAP: '/api/v1/embedded/session/bootstrap',
  EMBEDDED_ONBOARD: '/api/v1/embedded/onboard',

  // Scripts
  SCRIPTS_PARSE: '/api/v1/scripts/parse',
  SCRIPTS: '/api/v1/scripts',
  SCRIPTS_BY_ID: (id: string) => `/api/v1/scripts/${id}`,
  SCRIPTS_DUPLICATE: (id: string) => `/api/v1/scripts/${id}/duplicate`,
  SCRIPTS_STEPS: (scriptId: string) => `/api/v1/scripts/${scriptId}/steps`,
  SCRIPTS_STEPS_BY_ID: (scriptId: string, stepId: string) =>
    `/api/v1/scripts/${scriptId}/steps/${stepId}`,
  SCRIPTS_STEPS_REORDER: (scriptId: string) =>
    `/api/v1/scripts/${scriptId}/steps/reorder`,
  SCRIPTS_PHRASES: (scriptId: string, stepId: string) =>
    `/api/v1/scripts/${scriptId}/steps/${stepId}/phrases`,
  SCRIPTS_PHRASES_BY_ID: (scriptId: string, stepId: string, phraseId: string) =>
    `/api/v1/scripts/${scriptId}/steps/${stepId}/phrases/${phraseId}`,

  // Project Scripts (assignment)
  PROJECT_SCRIPTS: (projectId: string) => `/api/v1/projects/${projectId}/scripts`,
  PROJECT_SCRIPTS_BY_ID: (projectId: string, scriptId: string) =>
    `/api/v1/projects/${projectId}/scripts/${scriptId}`,
  PROJECT_SCRIPTS_DEFAULT: (projectId: string, scriptId: string) =>
    `/api/v1/projects/${projectId}/scripts/${scriptId}/default`,

  // Access Requests
  ACCESS_REQUESTS: '/api/v1/users/access-requests',
  ACCESS_REQUESTS_APPROVE: (id: string) => `/api/v1/users/access-requests/${id}/approve`,
  ACCESS_REQUESTS_REJECT: (id: string) => `/api/v1/users/access-requests/${id}/reject`,
  EMBEDDED_ACCESS_REQUEST: '/api/v1/embedded/access-request',
  EMBEDDED_ACCESS_REQUEST_STATUS: '/api/v1/embedded/access-request/status',

  // Notifications
  NOTIFICATIONS: '/api/v1/notifications',
  NOTIFICATIONS_RECENT: '/api/v1/notifications/recent',
  NOTIFICATIONS_READ: (id: string) => `/api/v1/notifications/${id}/read`,
  NOTIFICATIONS_COUNT: '/api/v1/notifications/count',
  NOTIFICATIONS_READ_ALL: '/api/v1/notifications/read-all',

  // Prompt Profiles
  PROMPT_PROFILES: '/api/v1/prompt-profiles',
  PROMPT_PROFILES_BY_ID: (id: string) => `/api/v1/prompt-profiles/${id}`,
  PROMPT_PROFILES_DUPLICATE: (id: string) => `/api/v1/prompt-profiles/${id}/duplicate`,
  PROMPT_PROFILES_PREVIEW_ANALYZE: '/api/v1/prompt-profiles/preview-analyze',
  PROMPT_PROFILES_RESTORE_DEFAULT: '/api/v1/prompt-profiles/restore-default',

  // Provider Credentials
  PROVIDER_CREDENTIALS: (orgId: string) =>
    `/api/v1/organizations/${orgId}/provider-credentials`,
  PROVIDER_CREDENTIALS_BY_TYPE: (orgId: string, type: string, name: string) =>
    `/api/v1/organizations/${orgId}/provider-credentials/${type}/${name}`,

  // CRM Import
  CRM_IMPORT_HISTORY: '/api/v1/crm/import/history',
  CRM_IMPORT_INITIATE: '/api/v1/crm/import/initiate',
  CRM_IMPORT_ESTIMATE: '/api/v1/crm/import/estimate',
  CRM_IMPORT_STATUS: (jobId: string) => `/api/v1/crm/import/status/${jobId}`,
  CRM_IMPORT_CANCEL: (jobId: string) => `/api/v1/crm/import/cancel/${jobId}`,

  // CRM Stages Reference (Phase B, B-6) — org's deal pipelines/stages, each
  // annotated with a suggested stage-semantic. Powers the stage-bindings
  // authoring UI (B-7) and the stage badge on the analysis surface.
  CRM_STAGES: '/api/v1/crm/stages',
  CRM_STAGE_NAMES: '/api/v1/crm/stage-names',
  CRM_STAGE_SEMANTICS: '/api/v1/crm/stage-semantics',
  // Sales methodology of a pipeline (#766): every stored one, and one by id.
  CRM_SALES_METHODOLOGIES: '/api/v1/crm/sales-methodology',
  CRM_PIPELINE_SALES_METHODOLOGY: (pipelineId: string) =>
    `/api/v1/crm/pipelines/${encodeURIComponent(pipelineId)}/sales-methodology`,

  // Stage Scorecard Bindings (Phase B, B-6) — authoring CRUD for mapping a
  // CRM pipeline+stage (exact) or a coarse stage-semantic (semantic
  // fallback) to a prompt profile.
  STAGE_BINDINGS: '/api/v1/stage-bindings',
  STAGE_BINDINGS_BY_ID: (id: string) => `/api/v1/stage-bindings/${id}`,

  // Search
  SEARCH: '/api/v1/search',

  // Delivery Config
  DELIVERY_CONFIG: (orgId: string) => `/api/v1/organizations/${orgId}/delivery/config`,
  DELIVERY_TEST: (orgId: string) => `/api/v1/organizations/${orgId}/delivery/test`,

  // Smart Process
  SMART_PROCESS_SETUP: '/api/v1/embedded/smart-process/setup',
  SMART_PROCESS_ORG: (orgId: string) => `/api/v1/organizations/${orgId}/smart-process`,
  SMART_PROCESS_ORG_SETUP: (orgId: string) =>
    `/api/v1/organizations/${orgId}/smart-process/setup`,
  SMART_PROCESS_ORG_SETUP_FIELDS: (orgId: string) =>
    `/api/v1/organizations/${orgId}/smart-process/setup-fields`,
  SMART_PROCESS_ORG_RECREATE: (orgId: string) =>
    `/api/v1/organizations/${orgId}/smart-process/recreate`,

  // CRM User Sync
  SYNC_CRM_USERS: (orgId: string) => `/api/v1/organizations/${orgId}/sync-crm-users`,

  // Org Structure (department tree behind the hierarchy access layer —
  // read, import from CRM, assign/clear a department head)
  ORG_STRUCTURE: (orgId: string) => `/api/v1/org-structure/${orgId}`,
  ORG_STRUCTURE_IMPORT: (orgId: string) => `/api/v1/org-structure/${orgId}/import`,
  ORG_STRUCTURE_DEPARTMENT_HEAD: (orgId: string, departmentId: string) =>
    `/api/v1/org-structure/${orgId}/departments/${departmentId}/head`,

  // Bitrix24 Fields. EMBEDDED ONLY: edge-api serves these routes solely at
  // /api/v1/embedded/bitrix24/fields (behind the embedded session) — there is
  // no standalone /api/v1/bitrix24/fields. The value below works because the
  // embedded client rewrites /api/v1/* to /api/v1/embedded/*
  // (useEmbeddedAuthFetch); a non-embedded caller would get a 404 (#1707).
  BITRIX24_FIELDS: '/api/v1/bitrix24/fields',

  // CRM Fields (cached metadata for QueryBuilder)
  CRM_FIELDS: '/api/v1/crm-fields',
  CRM_FIELDS_REFRESH: '/api/v1/crm-fields/refresh',
  CRM_FIELDS_ENTITY: (entityType: string, entityId: string) =>
    `/api/v1/crm-fields/entity/${entityType}/${entityId}`,
  CRM_FIELDS_ENTITY_TYPES: '/api/v1/crm-fields/entity-types',
  CRM_FIELDS_PROVIDERS: '/api/v1/crm-fields/providers',
  CRM_FIELDS_DEPARTMENTS: '/api/v1/crm-fields/departments',
  CRM_FIELDS_AGENT_DEPARTMENTS: '/api/v1/crm-fields/agent-departments',
  CRM_FIELDS_DEPARTMENTS_REFRESH: '/api/v1/crm-fields/departments/refresh',

  // Delivery Rules (CRM field updates after analysis)
  // Renamed from routing_rules in migration 035 for clarity:
  // - "Delivery" = what happens AFTER analysis (CRM field updates)
  // - "InboundRouting" = which PROJECT receives incoming records
  DELIVERY_RULES: '/api/v1/delivery-rules',
  DELIVERY_RULES_BY_ID: (id: string) => `/api/v1/delivery-rules/${id}`,
  DELIVERY_RULES_TOGGLE: (id: string) => `/api/v1/delivery-rules/${id}/toggle`,
  DELIVERY_RULES_REORDER: '/api/v1/delivery-rules/reorder',
  DELIVERY_RULES_ENTITY_TYPES: '/api/v1/delivery-rules/entity-types',

  // User Notification Channels (per-user, per-org notification preferences)
  NOTIFICATION_CHANNELS: '/api/v1/settings/notification-channels',
  NOTIFICATION_CHANNELS_TEST: '/api/v1/settings/notification-channels/test',
  NOTIFICATION_CHANNELS_GENERATE_SECRET:
    '/api/v1/settings/notification-channels/generate-secret',
  NOTIFICATION_CHANNELS_TELEGRAM_CONFIRM_CODE:
    '/api/v1/settings/notification-channels/telegram/confirm-code',

  // Telegram one-shot deep link (Task 8) — issues a link the user opens to
  // bind their chat, without ever needing to know their own chat id.
  INTEGRATIONS_TELEGRAM_LINK: '/api/v1/integrations/telegram/link',

  // Ingest Activity Log (webhook routing decisions audit trail)
  INGEST_LOG: '/api/v1/ingest-log',

  // Inbound Routing (determines which PROJECT receives incoming records)
  INBOUND_ROUTING: '/api/v1/inbound-routing',
  INBOUND_ROUTING_BY_ID: (id: string) => `/api/v1/inbound-routing/${id}`,
  INBOUND_ROUTING_TOGGLE: (id: string) => `/api/v1/inbound-routing/${id}/toggle`,
  INBOUND_ROUTING_REORDER: '/api/v1/inbound-routing/reorder',
  INBOUND_ROUTING_FALLBACK: '/api/v1/inbound-routing/fallback',

  // AI assistant (#765). The same paths serve embedded mode: the embedded
  // fetch rewrites `/api/v1/*` to `/api/v1/embedded/*`, where edge-api mounts
  // the router a second time.
  ASSISTANT_ASK: '/api/v1/assistant/ask',
  ASSISTANT_TURN: (turnId: string) => `/api/v1/assistant/turns/${turnId}`,
  ASSISTANT_THREADS: '/api/v1/assistant/threads',
  ASSISTANT_QUOTA: '/api/v1/assistant/quota',
  ASSISTANT_ENTITY_NAMES: '/api/v1/assistant/entity-names',

  // Demo Data
  DEMO_DATA_SEED: '/api/v1/demo-data/seed',
  DEMO_DATA_STATUS: '/api/v1/demo-data/status',
  DEMO_DATA_CLEAR: '/api/v1/demo-data/clear',
  DEMO_DATA_SWAP: '/api/v1/demo-data/swap',
  // Embedded-only: onboarding survey re-seeds the demo to the chosen industry.
  DEMO_DATA_ONBOARD: '/api/v1/demo-data/onboard',

  // Stripe
  STRIPE_CHECKOUT: '/api/v1/stripe/checkout',
  STRIPE_PORTAL: '/api/v1/stripe/portal',
  STRIPE_CREDIT_PURCHASE: '/api/v1/stripe/credit-purchase',
  STRIPE_SUBSCRIPTION: '/api/v1/stripe/subscription',
  STRIPE_SUBSCRIPTION_CANCEL: '/api/v1/stripe/subscription/cancel',
  STRIPE_SUBSCRIPTION_REACTIVATE: '/api/v1/stripe/subscription/reactivate',
  STRIPE_SUBSCRIPTION_CHANGE: '/api/v1/stripe/subscription/change',
  STRIPE_SUBSCRIPTION_CHANGE_PREVIEW: '/api/v1/stripe/subscription/change/preview',
} as const;
